import { useState, useEffect, useRef } from 'react';

interface TimeSlot {
  time: string;
  available: boolean;
}

const CalendarSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', contact: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const timeSlots: TimeSlot[] = [
    { time: '09:00', available: true },
    { time: '10:00', available: true },
    { time: '11:00', available: false },
    { time: '12:00', available: true },
    { time: '14:00', available: true },
    { time: '15:00', available: true },
    { time: '16:00', available: false },
    { time: '17:00', available: true },
  ];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);

  const monthNames = [
    'Януари', 'Февруари', 'Март', 'Април', 'Май', 'Юни',
    'Юли', 'Август', 'Септември', 'Октомври', 'Ноември', 'Декември'
  ];

  const dayNames = ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleDateSelect = (day: number) => {
    const selected = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selected >= today) {
      setSelectedDate(selected);
      setSelectedTime('');
      setShowForm(false);
    }
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime || !formData.name || !formData.contact) return;

    setIsSubmitting(true);

    const formElement = e.target as HTMLFormElement;
    const submitData = new URLSearchParams();
    submitData.append('name', formData.name);
    submitData.append('contact', formData.contact);
    submitData.append('date', selectedDate.toLocaleDateString('bg-BG'));
    submitData.append('time', selectedTime);

    try {
      await fetch('https://readdy.ai/api/form/d6g4q5qn5jm489bu5ui0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString(),
      });

      setShowSuccess(true);
      setFormData({ name: '', contact: '' });
      setSelectedDate(null);
      setSelectedTime('');
      setShowForm(false);

      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Грешка при изпращане:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDateDisabled = (day: number) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isDateSelected = (day: number) => {
    if (!selectedDate) return false;
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === currentDate.getMonth() &&
      selectedDate.getFullYear() === currentDate.getFullYear()
    );
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-24 overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900"
    >
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-96 h-96 bg-amber-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-5xl font-bold text-white mb-4 font-serif">
            Запишете се за консултация
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Изберете удобна дата и час за персонална среща
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Calendar */}
            <div
              className={`transition-all duration-1000 delay-200 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}
            >
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-amber-500/20 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={handlePrevMonth}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 transition-all duration-300 hover:scale-110"
                    type="button"
                  >
                    <i className="ri-arrow-left-s-line text-xl"></i>
                  </button>
                  <h3 className="text-2xl font-bold text-white">
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h3>
                  <button
                    onClick={handleNextMonth}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 transition-all duration-300 hover:scale-110"
                    type="button"
                  >
                    <i className="ri-arrow-right-s-line text-xl"></i>
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-2 mb-4">
                  {dayNames.map((day) => (
                    <div key={day} className="text-center text-amber-400 font-semibold text-sm py-2">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-2">
                  {Array.from({ length: startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1 }).map((_, index) => (
                    <div key={`empty-${index}`} className="aspect-square"></div>
                  ))}
                  {Array.from({ length: daysInMonth }).map((_, index) => {
                    const day = index + 1;
                    const disabled = isDateDisabled(day);
                    const selected = isDateSelected(day);

                    return (
                      <button
                        key={day}
                        onClick={() => handleDateSelect(day)}
                        disabled={disabled}
                        className={`aspect-square rounded-lg font-semibold transition-all duration-300 whitespace-nowrap ${
                          disabled
                            ? 'text-gray-600 cursor-not-allowed'
                            : selected
                            ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/50 scale-110'
                            : 'text-white hover:bg-white/10 hover:scale-105'
                        }`}
                        type="button"
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Time Slots & Form */}
            <div
              className={`transition-all duration-1000 delay-400 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
              }`}
            >
              {selectedDate && (
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-amber-500/20 shadow-2xl mb-6">
                  <h3 className="text-xl font-bold text-white mb-4">
                    Избрана дата: {selectedDate.toLocaleDateString('bg-BG')}
                  </h3>
                  <p className="text-gray-300 mb-6">Изберете удобен час:</p>
                  <div className="grid grid-cols-2 gap-3">
                    {timeSlots.map((slot) => (
                      <button
                        key={slot.time}
                        onClick={() => slot.available && handleTimeSelect(slot.time)}
                        disabled={!slot.available}
                        className={`py-3 px-4 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap ${
                          !slot.available
                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                            : selectedTime === slot.time
                            ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/50 scale-105'
                            : 'bg-white/10 text-white hover:bg-white/20 hover:scale-105'
                        }`}
                        type="button"
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {showForm && (
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-amber-500/20 shadow-2xl animate-fadeIn">
                  <h3 className="text-xl font-bold text-white mb-6">Вашите данни</h3>
                  <form onSubmit={handleSubmit} data-readdy-form id="consultation-booking">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-gray-300 mb-2 font-semibold">Име</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-amber-500 transition-all duration-300"
                          placeholder="Вашето име"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-300 mb-2 font-semibold">Контакт (телефон или email)</label>
                        <input
                          type="text"
                          name="contact"
                          value={formData.contact}
                          onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                          required
                          className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-amber-500 transition-all duration-300"
                          placeholder="Телефон или email"
                        />
                      </div>
                      <input type="hidden" name="date" value={selectedDate?.toLocaleDateString('bg-BG') || ''} />
                      <input type="hidden" name="time" value={selectedTime} />
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold rounded-lg hover:shadow-2xl hover:shadow-amber-500/50 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {isSubmitting ? 'Изпращане...' : 'Потвърдете записването'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {!selectedDate && (
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-amber-500/20 shadow-2xl flex items-center justify-center min-h-[300px]">
                  <div className="text-center">
                    <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-amber-500/20 rounded-full">
                      <i className="ri-calendar-line text-4xl text-amber-400"></i>
                    </div>
                    <p className="text-gray-300 text-lg">
                      Изберете дата от календара
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showSuccess && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-gradient-to-br from-slate-900 to-blue-950 rounded-2xl p-8 max-w-md mx-4 border border-amber-500/30 shadow-2xl animate-scaleIn">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full shadow-lg shadow-amber-500/50">
                <i className="ri-check-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Успешно записване!</h3>
              <p className="text-gray-300 mb-6">
                Вашата консултация е потвърдена. Ще се свържем с вас скоро.
              </p>
              <button
                onClick={() => setShowSuccess(false)}
                className="px-8 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold rounded-lg hover:shadow-xl hover:shadow-amber-500/50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                type="button"
              >
                Затвори
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CalendarSection;
